import React, { useState } from 'react';
import { ChevronDown, Mail } from 'lucide-react';

const FAQItem = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleFAQ = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      data-aos="fade-up"
      data-aos-delay={delay}
      className="glass-card rounded-2xl shadow-xl overflow-hidden relative z-10 animate-fadeInUp ripple-effect"
      style={{ animationDelay: `${delay}ms` }}
    >
      <button
        className="faq-question w-full px-8 py-6 text-left flex justify-between items-center hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-300 group"
        onClick={toggleFAQ}
      >
        <span className="font-bold text-gray-900 dark:text-white text-lg">
          {question}
        </span>
        <ChevronDown
          className={`text-[#4285F4] transition-transform duration-300 h-6 w-6 group-hover:scale-110 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`faq-answer px-8 transition-all duration-300 ${isOpen ? 'pb-6 max-h-96' : 'max-h-0 overflow-hidden'}`}>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const faqs = [
    {
      question: "Is GLabs Automator free to use?",
      answer: "GLabs Automator offers both free and premium versions. The free version includes basic automation features, while the premium version unlocks advanced functionality and higher generation limits.",
      delay: 0
    },
    {
      question: "What AI tools does it support?",
      answer: "Currently, GLabs Automator supports Google Labs tools including Whisk, ImageFX, MusicFX, and Flow (Veo 2 & 3). We're continuously adding support for more AI tools based on user demand.",
      delay: 100
    },
    {
      question: "How does the bulk processing work?",
      answer: "You can upload a CSV file with multiple prompts or paste them directly. The extension will process them sequentially, generating content for each prompt. Results are compiled into an organized CSV file for easy download.",
      delay: 200
    },
    {
      question: "Is my data secure?",
      answer: "Yes, GLabs Automator is fully compliant with Chrome Web Store policies and Google's privacy standards. We don't store your prompts or generated content on our servers - everything is processed locally in your browser.",
      delay: 300
    },
    {
      question: "Can I use this for commercial purposes?",
      answer: "Absolutely! Many of our users generate content for commercial use including stock images, print-on-demand products, YouTube videos, and music tracks. Please review the terms of service for each AI tool regarding commercial usage.",
      delay: 400
    }
  ];

  return (
    <section id="faq" className="py-20 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      {/* Google-themed ambient backgrounds */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#EA4335]"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#FBBC04]" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full blur-3xl animate-pulse bg-[#34A853]" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Frequently Asked <span className='text-[#EA4335]'>Questions</span>
          </h2>
          <p className="text-lg text-gray-700 font-medium dark:text-gray-300">
            Find answers to common questions about GLabs Automator
          </p>
        </div>
        
        <div className="space-y-6">
          {faqs.map((faq, index) => (
  <FAQItem
    key={`${faq.question}-${faq.answer}-${index}`}
    question={faq.question}
    answer={faq.answer}
    delay={faq.delay}
  />
))}

        </div>
        
        <div className="mt-12 text-center animate-fadeInUp" style={{ animationDelay: '600ms' }}>
          <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg font-medium">Still have questions?</p>
          <a
            href="https://intercom.help/connectautoplaylabs/en" target='_blank' rel='noreferrer'
            className="inline-flex items-center bg-gradient-to-r from-[#34A853] to-[#0f9d58] dark:from-[#8B5CF6] dark:to-[#7C3AED] font-bold text-white px-8 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 ripple-effect group"
          >
            <Mail className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Contact Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
